import React from 'react';

import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';

import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  CardContent,
  Container,
  Grid,
  Typography,
} from '@mui/material';

import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-mui';
import * as Yup from 'yup';

import TopNav from '../components/TopNav';

import BottomNav from '../components/BottomNav';
import Link from '../components/Link';
import GoogleMaps from '../components/GoogleMaps';

import mobileRegex from '../constants/mobileRegex';

import Onboard from '../components/auth/Onboard';
import axiosInstance from '../lib/axiosInstance';
import gaEvent from '../lib/gaEvent';
import useAuth from '../contexts/auth';


const checkBusinessName = async (value) => {
  const { data: { existing } } = await axiosInstance(
    `/api/v2/check-business-name/`, {
      params: {
        name: value
      }
    }
  )
  return !existing
}


const RegisterSchema = Yup.object().shape({
  name: Yup.string()
    .max(100, 'Only 100 characters allowed')
    .required('Business name is required')
    .test(
      'nameExists',
      'A business with this name is already registered',
      checkBusinessName
    ),
  mobile: Yup.string()
    .matches(mobileRegex, 'The mobile number you provided is not valid')
    .max(16, 'This number is to long')
    .required('Mobile number is required'),
  telephone: Yup.string()
    .matches(mobileRegex, 'The telephone number you provided is not valid')
    .max(16, 'This number is to long')
    .required('Telephone number is required'),
  address: Yup.string().required('Address is required'),
  email: Yup.string()
});


const RegisterBusiness = () => {
  const router = useRouter();
  const [authOpened, setAuthOpened] = React.useState(false);
  const { isAuthenticated } = useAuth();

  const openAuth = () => {
    setAuthOpened(true);
  };

  const closeAuth = (event, reason, sent) => {
    if (sent) return;
    setAuthOpened(false);
    gaEvent({
      category: 'Register Business',
      action: 'Closed Auth',
      label: 'Closed Auth',
    });
  };

  return (
    <>
      <Head>
        <title>Register Your Business - MechanicBuddy</title>
        <meta
          name="description"
          content="MechanicBuddy connects mechanics and workshops with new user."
        />

        {/* Open Graph / Facebook */}
        {/* <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.mechanicbuddy.co.za/register-business" />
        <meta property="og:title" content="Register Business | MechanicBuddy" />
        <meta
          property="og:description"
          content="We connect you with local reputable mechanics and workshops. Get a quick cost estimate or request your own quotes."
        />
        <meta
          property="og:image"
          content="https://www.mechanicbuddy.co.za/logos/mechanicbuddy-text-logo-square.jpg"
        /> */}

        {/* Twitter */}
        {/* <meta property="twitter:card" content="summary" />
        <meta property="twitter:url" content="https://www.mechanicbuddy.co.za/register-business" />
        <meta property="twitter:title" content="Register Business | MechanicBuddy" />
        <meta
          property="twitter:description"
          content="We connect you with local reputable mechanics and workshops. Get a quick cost estimate or request your own quotes."
        />
        <meta
          property="twitter:image"
          content="https://www.mechanicbuddy.co.za/logos/mechanicbuddy-text-logo-square.jpg"
        /> */}

        <link rel="canonical" href="https://www.mechanicbuddy.co.za/register-business/" />
      </Head>

      <Box display="flex" flexDirection="column" minHeight="100vh">
        <TopNav />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            width: '100%',
            bgcolor: 'primary.main',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundImage: {
              md: 'url("/img/register-background.jpg")'
            },
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              my: 4,
              '& .MuiTextField-root': {
                mb: 2,
              },
            }}
          >
            <Grid container columnSpacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box color="white" textAlign="center">
                  <Typography variant="h3" component="h1" gutterBottom textAlign="center">
                    Grow your business
                  </Typography>
                  <Image
                    src="/icons/post-requests.svg"
                    width={200}
                    height={200}
                    alt="Potential clients"
                    title="Potential clients"
                    loading="lazy"
                  />
                  <Typography variant="h6">
                    Getting started is quick
                  </Typography>
                  <Typography variant="body1">
                    Tell us what cars you work on and what services you provide and we&apos;ll deliver the local customers to your inbox.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box fontSize="h4.fontSize" textAlign="center" p={2}>
                      Register your business
                    </Box>
                    <Formik
                      initialValues={{
                        name: '',
                        mobile: '',
                        telephone: '',
                        address: '',
                        email: '',
                      }}
                      enableReinitialize
                      validationSchema={RegisterSchema}
                      onSubmit={(values, { setSubmitting }) => {
                        console.log(values);

                        axiosInstance.post(`/api/v2/create-application/`, values)
                          .then(() => {
                            setSubmitting(false);
                            gaEvent({
                              category: 'Register Business',
                              action: 'Application Created',
                              label: 'Application Created',
                            });
                            if (!values.email) {
                              router.push('/hub/onboard/');
                            }
                          })
                          .catch(error => {
                            setSubmitting(false);
                            gaEvent({
                              category: 'Register Business',
                              action: 'Application Failed',
                              label: 'Application Failed',
                            });
                            console.log('error', error);
                          });
                      }}
                    >
                      {({ submitForm, isSubmitting, setFieldValue, isValid, dirty }) => (
                        <Form>
                          <Field
                            component={TextField}
                            label="Business Name"
                            name="name"
                            variant="outlined"
                            fullWidth
                          />
                          <Field
                            component={TextField}
                            name="mobile"
                            label="Mobile Number"
                            variant="outlined"
                            fullWidth
                          />
                          <Field
                            component={TextField}
                            name="telephone"
                            label="Telephone Number"
                            variant="outlined"
                            fullWidth
                          />
                          <GoogleMaps
                            setFieldValue={setFieldValue}
                            initialValue=""
                            autoFocus={false}
                            placeholder="Address"
                            name="address"
                          />

                          <Box m={1} fontSize="10px" color="text.secondary">
                            By continuing you agree to MechanicBuddy&apos;s <Link href="/garage-terms-and-conditions/">Terms of Service</Link>
                          </Box>

                          <Backdrop
                            open={isSubmitting}
                            sx={{
                              zIndex: theme => theme.zIndex.drawer + 1,
                              color: '#fff',
                            }}
                          >
                            <CircularProgress color="inherit" />
                          </Backdrop>

                          <Box display="flex" justifyContent="center" mt={2}>
                            <Button
                              variant="contained"
                              color="secondary"
                              disabled={!(isValid && dirty) || isSubmitting}
                              onClick={() => {
                                gaEvent({
                                  category: 'Register Business',
                                  action: 'Click Submit',
                                  label: 'Submit Details',
                                });
                                if (isAuthenticated) {
                                  submitForm();
                                } else {
                                  openAuth();
                                }
                              }}
                            >
                              Submit
                            </Button>
                          </Box>

                          <Onboard 
                            open={authOpened} 
                            handleClose={closeAuth}
                            setFieldValue={setFieldValue}
                            submitForm={submitForm}
                          />
                        </Form>
                      )}
                    </Formik>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
      <BottomNav hideLogo />
    </>
  );
}

export default RegisterBusiness;
